import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchHealth } from '../api.js';

/**
 * Zeigt, ob Web, API und Datenbank wirklich miteinander reden.
 *
 * In E0 ist das der einzige echte Nachweis, den die Oberfläche führen kann — und
 * er ist mehr wert als eine hübsche leere Ansicht: er prüft die ganze Kette.
 */
export function SystemStatus(): React.JSX.Element {
  const { t } = useTranslation();
  const health = useQuery({
    queryKey: ['health'],
    queryFn: fetchHealth,
    retry: false,
    refetchInterval: 30_000,
  });

  // „Verbunden" ist gut, nicht auffällig — deshalb `good` und nicht `accent`.
  // Der Bernstein ist in dieser Gestaltung das Signal für die nächste Handlung;
  // ihn hier zu benutzen hieße, dass ein gesunder Server etwas von einem will.
  const tone = health.isError
    ? 'text-danger'
    : health.data?.status === 'ok'
      ? 'text-good'
      : 'text-warn';

  const label = health.isPending
    ? t('status.checking')
    : health.isError
      ? t('status.offline')
      : health.data?.status === 'ok'
        ? t('status.ok')
        : t('status.degraded');

  return (
    <section aria-labelledby="status-heading" className="kw-tile">
      <h2 id="status-heading" className="kw-kicker">
        {t('status.heading')}
      </h2>

      <p className={`kw-figure mt-1.5 text-2xl ${tone}`} role="status" aria-live="polite">
        {label}
      </p>

      {health.data ? (
        <dl className="mt-2 flex gap-2 text-sm text-ink-muted">
          <dt>{t('status.database')}:</dt>
          <dd>{health.data.checks.database}</dd>
        </dl>
      ) : null}

      <button
        type="button"
        onClick={() => void health.refetch()}
        className="kw-btn kw-btn-quiet kw-btn-small mt-3 w-full"
      >
        {t('status.retry')}
      </button>
    </section>
  );
}
